import { useEffect, useRef, useState } from "react";
import { FocusTrap } from "focus-trap-react";
import { AdditionalProductsInterface, CartConfigInterface, CartLineItem } from "../CartTypes";
import styles from "./CartDrawer.module.css";
import { custom_field, ProductObj } from "../../Bundles/Types";
import {
    fetchProductsByIds,
    getProductsDynamicallyAndReturnNewProductsArray,
    updateBundleStats,
} from "../../Bundles/constants";
import Spinner from "../../../components/Icons/Spinner";
import useCartDrawerLayoutHook from "../../Bundles/useCartDrawerLayoutHook";
import { createPortal } from "react-dom";
import { IoMdEye } from "react-icons/io";
import { IoChevronDown, IoChevronUp, IoClose } from "react-icons/io5";
import config from "../../../config";

const RenderAdditionalProductImage = ({ image, size = 35 }: { image: any; size: number }) => {
    return (
        <span
            style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: size,
                maxWidth: size,
                aspectRatio: 1,
            }}
        >
            <img src={image?.originalSource} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
        </span>
    );
};

interface ToggleButtonProps {
    status: boolean;
    handler: (e: any) => void;
    label?: string;
    disabled?: boolean;
    colors?: any;
}

interface AddonModalProps {
    isOpen: boolean;
    onClose: () => void;
    onRemove: () => void;
    additional_product: AdditionalProductsInterface;
    selectedAddon: ProductObj | null;
    setSelectedAddon: (product: ProductObj | null) => void;
    onConfirm: (data: { product: ProductObj | null; fieldValues: Record<string, string> }) => void;
    cartConfig: CartConfigInterface;
    loadingAddon: boolean;
    localCustomFieldValues: Record<string, string>;
    setLocalCustomFieldValues: (values: Record<string, string>) => void;
    money_format: string;
    additionalAddonProducts: ProductObj[];
}

const ToggleButton: React.FC<ToggleButtonProps> = ({ status, handler, label, disabled = false, colors = {} }) => {
    return (
        <div
            className={`${styles.toggleContainer} ${disabled && styles.disabled}`}
            style={{
                // @ts-expect-error hack
                "--sliderColor": colors?.sliderColor || "red",
            }}
        >
            {label && <span className={styles.label}>{label}</span>}
            <button
                className={`${styles.toggle} ${status ? styles.active : ""}`}
                onClick={disabled ? () => {} : handler}
                type="button"
                role="switch"
                aria-checked={status}
                style={{
                    // @ts-expect-error hack
                    "--sliderColor": colors?.sliderColor || "red",
                }}
            >
                <span
                    className={styles.slider}
                    style={{
                        // @ts-expect-error hack
                        "--sliderColor": colors?.sliderColor || "red",
                    }}
                />
            </button>
        </div>
    );
};

const AdditionalProductComponent = ({
    cartConfig,
    handleAdditionalProductAdd,
    sft,
    standAlone,
    money_format,
    cartLines,
    customShippingProtection = false,
    addToCartFromCart = () => {},
    changeCartItem,
}: {
    handleAdditionalProductAdd: ({
        state,
        additionalProduct,
    }: {
        state: boolean;
        additionalProduct: ProductObj;
    }) => Promise<void>;
    cartConfig: CartConfigInterface;
    sft: string;
    standAlone: boolean;
    money_format: string;
    cartLines: CartLineItem[];
    customShippingProtection?: boolean;
    addToCartFromCart?: any;
    changeCartItem?: any;
}) => {
    const { cartAdditionalLayout } = useCartDrawerLayoutHook({ config: cartConfig });
    const [toggleStatus, setToggleStatus] = useState(window.sessionStorage.getItem("ap_status") == "on" ? true : false);
    const [currentProduct, setCurrentProduct] = useState<ProductObj | null>(!standAlone ? ({} as ProductObj) : null);
    const [loading, setLoading] = useState(false);

    const [isAddonModalOpen, setIsAddonModalOpen] = useState(false);
    const [selectedAddon, setSelectedAddon] = useState<ProductObj | null>(null);
    const [additionalAddonProducts, setAdditionalAddonProducts] = useState<ProductObj[]>(
        cartConfig?.additional_product?.additional_addon_products || [],
    );

    const [loadingAddon, setLoadingAddon] = useState(false);
    const [localCustomFieldValues, setLocalCustomFieldValues] = useState<Record<string, string>>({});

    const handleOpenAddonModal = () => {
        if (additionalAddonProducts?.length === 0) return;
        setIsAddonModalOpen(true);
    };

    const handleCloseAddonModal = () => {
        setIsAddonModalOpen(false);
    };

    const handleConfirmAddonSelection = async (data: {
        product: ProductObj | null;
        fieldValues: Record<string, string>;
    }) => {
        if (!data.product) return;

        if (!standAlone) {
            setSelectedAddon(data.product);
            setLoadingAddon(false);
            setIsAddonModalOpen(false);
            return;
        }

        setLoadingAddon(true);
        setIsAddonModalOpen(false);

        try {
            const properties = { ...data.fieldValues };

            if (selectedAddon) {
                const existingLineIndex = cartLines?.findIndex(
                    (line) =>
                        String(line.variant_id) === String(selectedAddon.selectedVariants?.[0]?.id.split("/").pop()),
                );

                if (existingLineIndex !== undefined && existingLineIndex >= 0) {
                    await changeCartItem({ line: existingLineIndex + 1, quantity: 0 });
                }
            }

            const res = await addToCartFromCart(
                [
                    {
                        ...data.product,
                        selectedVariants: [data.product.selectedVariants[0]],
                        properties: data.fieldValues,
                    },
                ],
                properties,
                true,
                false,
            );

            if (res?.error) return; // stop if failed

            window.localStorage.setItem(
                "selectedAddon",
                JSON.stringify({
                    variantId: data.product.selectedVariants[0].id,
                    properties: data.fieldValues,
                }),
            );

            setSelectedAddon(data.product);
        } finally {
            setLoadingAddon(false);
        }
    };

    const handleRemoveAddonSelection = async () => {
        if (!standAlone || !selectedAddon) {
            setSelectedAddon(null);
            setLoadingAddon(false);
            setIsAddonModalOpen(false);
            return;
        }

        setLoadingAddon(true);
        setIsAddonModalOpen(false);

        try {
            const numericId = Number(selectedAddon.selectedVariants[0].id.split("/").pop());
            const lineIndex = cartLines?.findIndex((line) => String(line.variant_id) === String(numericId));

            if (lineIndex !== undefined && lineIndex >= 0) {
                const res = await changeCartItem({ line: lineIndex + 1, quantity: 0 });
                if (!res?.error) {
                    window.localStorage.removeItem("selectedAddon");
                    setSelectedAddon(null);
                }
            } else {
                window.localStorage.removeItem("selectedAddon");
                setSelectedAddon(null);
            }
        } finally {
            setLoadingAddon(false);
        }
    };

    useEffect(() => {
        const fetchUpdatedProducts = async () => {
            const addonProducts = cartConfig?.additional_product?.additional_addon_products;
            if (!addonProducts?.length) return;

            const ids = addonProducts.map((p) => p.id);

            try {
                const updatedArr = await fetchProductsByIds(ids, standAlone, sft); // latest from Shopify

                const updatedProducts = addonProducts.map((p) => {
                    const fresh = updatedArr.find((fp: any) => fp.id === p.id);
                    if (!fresh) return p;
                    return {
                        ...p,
                        title: fresh.title,
                        variants: p.variants.map((variant) => {
                            const freshVariant = fresh.variants.nodes.find((fv: any) => fv.id === variant.id);
                            if (!freshVariant) return variant;

                            return {
                                ...variant,
                                title: freshVariant.title,
                                displayName: fresh.title + " - " + freshVariant.title,
                                price: freshVariant.price.amount,
                                compareAtPrice: freshVariant.compareAtPrice?.amount ?? freshVariant.price.amount,
                                image: freshVariant.image?.url ? { src: freshVariant.image.url } : variant.image, // update image
                            };
                        }),
                    };
                });

                setAdditionalAddonProducts(updatedProducts);
            } catch (err) {
                console.error("Failed to fetch products", err);
            }
        };

        fetchUpdatedProducts();
    }, [cartConfig?.additional_product?.additional_addon_products, sft]);

    useEffect(() => {
        if (!standAlone || !cartConfig?.additional_product?.status) return;
        if (!cartConfig?.additional_product?.additional_product_addon_checkbox) return;
        if (additionalAddonProducts.length === 0) return;

        updateBundleStats({
            id: null,
            key: "views",
            view_type: 2,
            shop: window.kefi_bundles?.shop,
            appName: config.appName,
            product_ids: additionalAddonProducts
                .map((p) => p.variants?.[0]?.id)
                .filter(Boolean)
                .join(","),
        });
    }, [additionalAddonProducts.length]);

    useEffect(() => {
        if (!selectedAddon?.id) return;

        const numericId = Number(selectedAddon.selectedVariants[0].id.split("/").pop());
        const addonInCart = cartLines?.some((line) => line.variant_id === numericId);

        if (!addonInCart) {
            // addon removed outside our code , so clean up
            window.localStorage.removeItem("selectedAddon");
            setSelectedAddon(null);
        }

        setLoadingAddon(false);
    }, [cartLines]);

    useEffect(() => {
        const savedAddon = window.localStorage.getItem("selectedAddon");
        if (!savedAddon) return;

        const parsed = JSON.parse(savedAddon); // { productId, variantId, properties }
        setLocalCustomFieldValues(parsed.properties);

        // Find product containing this variant
        const productInCart = additionalAddonProducts?.find((p) => p.variants.some((v) => v.id === parsed.variantId));

        if (productInCart) {
            // Find the exact variant
            const selectedVariant = productInCart.variants.find((v) => v.id === parsed.variantId);

            // Ensure we attach the selected variant properly
            setSelectedAddon({
                ...productInCart,
                selectedVariants: selectedVariant ? [selectedVariant] : [],
            });
        }
    }, [additionalAddonProducts]);

    useEffect(() => {
        setLoading(false);
    }, [cartLines]);

    useEffect(() => {
        if (!standAlone) return;
        const getProductInfo = async (pId: null | string = null) => {
            if (!pId) {
                return;
            }
            const updateProduct = await getProductsDynamicallyAndReturnNewProductsArray([{ id: pId }], sft, true);
            setCurrentProduct(updateProduct[0]);
        };
        if (cartConfig?.additional_product?.product_type == "shipping_protection") {
            getProductInfo(cartConfig?.additional_product?.shipping_protection_id);
        } else if (cartConfig?.additional_product?.product_type == "gift_wrapping") {
            getProductInfo(cartConfig?.additional_product?.gift_wrapping_id);
        } else {
            setCurrentProduct(null);
        }
    }, [cartConfig?.additional_product?.product_type]);

    useEffect(() => {
        if (!standAlone || !currentProduct?.id || !cartConfig?.additional_product?.status) return;
        if (!cartConfig?.additional_product?.additional_product_shipping_gift_checkbox) return;

        const productId = currentProduct.variants?.[0]?.id;
        if (!productId) return;

        updateBundleStats({
            id: null,
            key: "views",
            view_type: 2,
            shop: window.kefi_bundles?.shop,
            appName: config.appName,
            product_ids: productId,
        });
    }, [currentProduct?.id]);

    useEffect(() => {
        if (!currentProduct?.id || !cartConfig?.additional_product) return;

        const { gift_wrapping_id, shipping_protection_id } = cartConfig.additional_product;

        const hasShippingProtection = cartLines?.some((item) =>
            shipping_protection_id?.includes(item?.product_id as unknown as string),
        );
        const hasGiftWrapping = cartLines?.some((item) =>
            gift_wrapping_id?.includes(item?.product_id as unknown as string),
        );
        setToggleStatus(window.sessionStorage.getItem("ap_status") == "on" ? true : false);

        setToggleStatus(hasGiftWrapping || hasShippingProtection || window.sessionStorage.getItem("ap_status") == "on");
    }, [currentProduct, cartLines]);

    return (
        <>
            {cartConfig?.additional_message?.status && !!cartConfig?.additional_message?.additional_msg_text && (
                <div
                    className={styles.additionalMessageContainer}
                    style={{
                        background: cartConfig?.additional_message?.additional_msg_bg,
                        color: cartConfig?.additional_message?.additional_msg_color,
                        fontSize: cartConfig?.additional_message?.additional_msg_text_size,
                        fontWeight: cartConfig?.additional_message?.additional_msg_text_style?.split(" ")[0],
                        fontStyle: cartConfig?.additional_message?.additional_msg_text_style?.split(" ")[1],
                        ...(cartAdditionalLayout.addGradientBorder && {
                            width: "91%",
                            marginInline: "auto",
                            borderRadius: 8,
                            marginBottom: 6,
                            border: `1px solid ${cartConfig?.additional_product_design?.additional_product_border_color}`,
                        }),
                    }}
                >
                    {cartConfig?.additional_message?.additional_msg_text}
                </div>
            )}

            {cartConfig?.additional_product?.status &&
                !!cartConfig?.additional_product?.additional_product_addon_checkbox &&
                additionalAddonProducts &&
                (standAlone ? additionalAddonProducts?.length > 0 : true) && (
                    <>
                        {additionalAddonProducts?.length > 0 ? (
                            <div
                                style={{
                                    position: "relative",
                                    ...(cartAdditionalLayout.addGradientBorder && {
                                        borderRadius: 8,
                                        padding: 2,
                                        background: cartConfig?.additional_product_design
                                            ?.additional_product_border_gradient_status
                                            ? `linear-gradient(${cartConfig?.additional_product_design?.additional_product_border_gradient_direction}deg, ${cartConfig?.additional_product_design?.additional_product_border_color} 0%, ${cartConfig?.additional_product_design?.additional_product_border_gradient_color} 100%)`
                                            : cartConfig?.additional_product_design?.additional_product_border_color,
                                        marginBottom: 5,
                                        width: "91%",
                                        marginInline: "auto",
                                    }),
                                }}
                            >
                                {standAlone && loadingAddon && (
                                    <div
                                        className={styles.cartItemLoading}
                                        style={{
                                            ...(cartAdditionalLayout.addGradientBorder && {
                                                borderRadius: 8,
                                            }),
                                        }}
                                    >
                                        <Spinner color={"#939393"} size={50} />
                                    </div>
                                )}

                                <div
                                    className={styles.productAddonWrapper}
                                    style={{
                                        ...(cartAdditionalLayout.addGradientBorder && {
                                            borderRadius: "inherit",
                                        }),
                                        background: cartConfig?.additional_product_design?.additional_product_card_bg,
                                    }}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleOpenAddonModal();
                                    }}
                                >
                                    <div className={styles.productAddonLeft}>
                                        <label className={styles.customCheckboxWrapper}>
                                            <input
                                                type="checkbox"
                                                checked={!!selectedAddon}
                                                readOnly
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    handleOpenAddonModal();
                                                }}
                                                className={styles.hiddenCheckbox}
                                            />

                                            <div
                                                className={styles.customCheckbox}
                                                style={{
                                                    borderColor: `${cartConfig?.additional_product_design?.additional_product_title_color}80`,
                                                    background: selectedAddon
                                                        ? cartConfig?.additional_product_design
                                                              ?.additional_product_title_color
                                                        : "",
                                                }}
                                            >
                                                {"\u00A0"}
                                            </div>
                                        </label>

                                        {!!additionalAddonProducts?.length && (
                                            <span
                                                style={{
                                                    display: "flex",
                                                    justifyContent: "center",
                                                    alignItems: "center",
                                                    width: cartAdditionalLayout?.iconSize,
                                                    maxWidth: cartAdditionalLayout?.iconSize,
                                                    aspectRatio: 1,
                                                    overflow: "hidden",
                                                    borderRadius: "4px",
                                                }}
                                            >
                                                <img
                                                    src={
                                                        selectedAddon?.selectedVariants?.[0]?.image?.src ||
                                                        additionalAddonProducts?.[0]?.images?.[0]?.originalSrc
                                                    }
                                                    alt={
                                                        selectedAddon?.selectedVariants?.[0]?.title ||
                                                        additionalAddonProducts?.[0]?.title
                                                    }
                                                    style={{ width: "100%", height: "100%", objectFit: "contain" }}
                                                />
                                            </span>
                                        )}
                                    </div>

                                    {/* Text section */}
                                    <div className={styles.productAddonRight}>
                                        <div
                                            style={{
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: cartAdditionalLayout.iconSize / 8,
                                            }}
                                        >
                                            {cartConfig?.additional_product?.additional_addon_title && (
                                                <span
                                                    className={styles.shippingProtectionTitle}
                                                    style={{
                                                        fontSize: `${cartConfig.additional_product_design.additional_product_title_size}px`,
                                                        color: cartConfig?.additional_product_design
                                                            ?.additional_product_title_color,
                                                        whiteSpace: "break-spaces",
                                                    }}
                                                    dangerouslySetInnerHTML={{
                                                        __html: (() => {
                                                            const title =
                                                                cartConfig?.additional_product
                                                                    ?.additional_addon_title ?? "";
                                                            const price =
                                                                selectedAddon?.selectedVariants?.[0]?.price ??
                                                                additionalAddonProducts?.[0]?.variants?.[0]?.price;

                                                            let formattedPrice = "";
                                                            if (price) {
                                                                formattedPrice = money_format.replace(
                                                                    /\{\{.*?\}\}/g,
                                                                    (
                                                                        parseFloat(price) *
                                                                        (window.kefi_bundles?.localization?.rate || 1)
                                                                    ).toFixed(2),
                                                                );
                                                            }

                                                            return title.replace("{productPrice}", formattedPrice);
                                                        })(),
                                                    }}
                                                ></span>
                                            )}

                                            {cartConfig?.additional_product?.additional_addon_subtext && (
                                                <span
                                                    className={styles.shippingProtectionSubtext}
                                                    style={{
                                                        fontSize:
                                                            cartConfig?.additional_product_design
                                                                ?.additional_product_description_size,
                                                        color: cartConfig?.additional_product_design
                                                            ?.additional_product_description_color,
                                                        fontWeight:
                                                            cartConfig?.additional_product_design?.additional_product_description_style?.split(
                                                                " ",
                                                            )[0],
                                                        fontStyle:
                                                            cartConfig?.additional_product_design?.additional_product_description_style?.split(
                                                                " ",
                                                            )[1],
                                                        whiteSpace: "break-spaces",
                                                    }}
                                                    dangerouslySetInnerHTML={{
                                                        __html: (() => {
                                                            const title =
                                                                cartConfig?.additional_product
                                                                    ?.additional_addon_subtext ?? "";
                                                            const price =
                                                                selectedAddon?.selectedVariants?.[0]?.price ??
                                                                additionalAddonProducts?.[0]?.variants?.[0]?.price;

                                                            let formattedPrice = "";
                                                            if (price) {
                                                                formattedPrice = money_format.replace(
                                                                    /\{\{.*?\}\}/g,
                                                                    (
                                                                        parseFloat(price) *
                                                                        (window.kefi_bundles?.localization?.rate || 1)
                                                                    ).toFixed(2),
                                                                );
                                                            }

                                                            return title.replace("{productPrice}", formattedPrice);
                                                        })(),
                                                    }}
                                                ></span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div
                                    style={{
                                        textAlign: "center",
                                        color: "#6b7280",
                                        fontSize: "14px",
                                        padding: "8px 0",
                                        fontWeight: "500",
                                        background: cartConfig?.additional_product_design?.additional_product_card_bg,
                                    }}
                                >
                                    Select an addon product!
                                </div>
                            </>
                        )}
                    </>
                )}

            {cartConfig?.additional_product?.status &&
                !!cartConfig?.additional_product?.product_type &&
                !!cartConfig?.additional_product?.additional_product_shipping_gift_checkbox &&
                !customShippingProtection && (
                    <div
                        style={{
                            position: "relative",
                            ...(cartAdditionalLayout.addGradientBorder && {
                                borderRadius: 8,
                                padding: 2,
                                background: cartConfig?.additional_product_design
                                    ?.additional_product_border_gradient_status
                                    ? `linear-gradient(${cartConfig?.additional_product_design?.additional_product_border_gradient_direction}deg, ${cartConfig?.additional_product_design?.additional_product_border_color} 0%, ${cartConfig?.additional_product_design?.additional_product_border_gradient_color} 100%) `
                                    : cartConfig?.additional_product_design?.additional_product_border_color,
                                marginBottom: 5,
                                width: "91%",
                                marginInline: "auto",
                            }),
                        }}
                    >
                        {standAlone && loading && (
                            <div
                                className={styles.cartItemLoading}
                                style={{
                                    ...(cartAdditionalLayout.addGradientBorder && {
                                        borderRadius: 8,
                                    }),
                                }}
                            >
                                <Spinner color={"#939393"} size={50} />
                            </div>
                        )}
                        {cartConfig?.additional_product?.product_type == "gift_wrapping" ? (
                            <div
                                className={styles.shippingProtectionWrapper}
                                style={{
                                    ...(cartAdditionalLayout.addGradientBorder && {
                                        borderRadius: "inherit",
                                    }),
                                    background: cartConfig?.additional_product_design?.additional_product_card_bg,
                                }}
                            >
                                <div className={styles.shippingProtectionLeft}>
                                    <RenderAdditionalProductImage
                                        image={cartConfig?.additional_product?.gift_wrapping_images[0]}
                                        size={cartAdditionalLayout?.iconSize}
                                    />

                                    <div
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: cartAdditionalLayout.iconSize / 8,
                                        }}
                                    >
                                        <span
                                            className={styles.shippingProtectionTitle}
                                            style={{
                                                fontSize:
                                                    cartConfig?.additional_product_design
                                                        ?.additional_product_title_size,
                                                color: cartConfig?.additional_product_design
                                                    ?.additional_product_title_color,
                                            }}
                                            dangerouslySetInnerHTML={{
                                                __html: cartConfig?.additional_product?.gift_wrapping_title,
                                            }}
                                        ></span>
                                        <span
                                            className={styles.shippingProtectionSubtext}
                                            style={{
                                                fontSize:
                                                    cartConfig?.additional_product_design
                                                        ?.additional_product_description_size,
                                                color: cartConfig?.additional_product_design
                                                    ?.additional_product_description_color,
                                                fontWeight:
                                                    cartConfig?.additional_product_design?.additional_product_description_style?.split(
                                                        " ",
                                                    )[0],
                                                fontStyle:
                                                    cartConfig?.additional_product_design?.additional_product_description_style?.split(
                                                        " ",
                                                    )[1],
                                            }}
                                            dangerouslySetInnerHTML={{
                                                __html: cartConfig?.additional_product?.gift_wrapping_subtext.replace(
                                                    "{price}",
                                                    money_format
                                                        .replace(
                                                            /\{\{.*?\}\}/g,
                                                            (
                                                                cartConfig?.additional_product?.gift_wrapping_price *
                                                                (window.kefi_bundles.localization.rate || 1)
                                                            )?.toFixed(2),
                                                        )
                                                        ?.toString(),
                                                ),
                                            }}
                                        ></span>
                                    </div>
                                </div>
                                <div className={styles.shippingProtectionRight}>
                                    <ToggleButton
                                        colors={{
                                            sliderColor: cartConfig?.cart_design?.header_bg_color,
                                        }}
                                        disabled={currentProduct == null || loading}
                                        handler={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            if (currentProduct != null && standAlone) {
                                                setToggleStatus((ps) => {
                                                    setLoading(true);
                                                    window.sessionStorage.setItem("ap_status", ps ? "off" : "on");
                                                    handleAdditionalProductAdd({
                                                        state: !ps,
                                                        additionalProduct: {
                                                            ...currentProduct,
                                                            selectedVariants: [currentProduct.variants[0]],
                                                        },
                                                    });
                                                    return !ps;
                                                });
                                            } else {
                                                setToggleStatus((ps) => !ps);
                                            }
                                        }}
                                        status={toggleStatus}
                                    />
                                </div>
                            </div>
                        ) : (
                            <div
                                className={styles.shippingProtectionWrapper}
                                style={{
                                    ...(cartAdditionalLayout.addGradientBorder && {
                                        borderRadius: "inherit",
                                    }),
                                    background: cartConfig?.additional_product_design?.additional_product_card_bg,
                                }}
                            >
                                <div className={styles.shippingProtectionLeft}>
                                    <RenderAdditionalProductImage
                                        image={cartConfig?.additional_product?.shipping_protection_images[0]}
                                        size={cartAdditionalLayout?.iconSize}
                                    />

                                    <div
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: cartAdditionalLayout.iconSize / 8,
                                        }}
                                    >
                                        {currentProduct?.handle}
                                        <span
                                            className={styles.shippingProtectionTitle}
                                            style={{
                                                fontSize:
                                                    cartConfig?.additional_product_design
                                                        ?.additional_product_title_size,
                                                color: cartConfig?.additional_product_design
                                                    ?.additional_product_title_color,
                                            }}
                                            dangerouslySetInnerHTML={{
                                                __html: cartConfig?.additional_product?.shipping_protection_title,
                                            }}
                                        ></span>
                                        <span
                                            className={styles.shippingProtectionSubtext}
                                            style={{
                                                fontSize:
                                                    cartConfig?.additional_product_design
                                                        ?.additional_product_description_size,
                                                color: cartConfig?.additional_product_design
                                                    ?.additional_product_description_color,
                                                fontWeight:
                                                    cartConfig?.additional_product_design?.additional_product_description_style?.split(
                                                        " ",
                                                    )[0],
                                                fontStyle:
                                                    cartConfig?.additional_product_design?.additional_product_description_style?.split(
                                                        " ",
                                                    )[1],
                                            }}
                                            dangerouslySetInnerHTML={{
                                                __html: cartConfig?.additional_product?.shipping_protection_subtext.replace(
                                                    "{price}",
                                                    money_format
                                                        .replace(
                                                            /\{\{.*?\}\}/g,
                                                            (
                                                                cartConfig?.additional_product
                                                                    ?.shipping_protection_price *
                                                                (window.kefi_bundles.localization.rate || 1)
                                                            )?.toFixed(2),
                                                        )
                                                        ?.toString(),
                                                ),
                                            }}
                                        ></span>
                                    </div>
                                </div>
                                <div className={styles.shippingProtectionRight}>
                                    <ToggleButton
                                        colors={{
                                            sliderColor: cartConfig?.cart_design?.header_bg_color,
                                        }}
                                        disabled={currentProduct == null || loading}
                                        handler={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            if (currentProduct != null && standAlone) {
                                                setToggleStatus((ps) => {
                                                    setLoading(true);
                                                    window.sessionStorage.setItem("ap_status", ps ? "off" : "on");
                                                    handleAdditionalProductAdd({
                                                        state: !ps,
                                                        additionalProduct: {
                                                            ...currentProduct,
                                                            selectedVariants: [currentProduct.variants[0]],
                                                        },
                                                    });
                                                    return !ps;
                                                });
                                            } else {
                                                setToggleStatus((ps) => !ps);
                                            }
                                        }}
                                        status={toggleStatus}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                )}

            {isAddonModalOpen && (
                <AddonModal
                    isOpen={isAddonModalOpen}
                    onClose={handleCloseAddonModal}
                    additional_product={cartConfig?.additional_product}
                    selectedAddon={selectedAddon}
                    setSelectedAddon={setSelectedAddon}
                    onConfirm={handleConfirmAddonSelection}
                    onRemove={handleRemoveAddonSelection}
                    cartConfig={cartConfig}
                    loadingAddon={loadingAddon}
                    localCustomFieldValues={localCustomFieldValues}
                    setLocalCustomFieldValues={setLocalCustomFieldValues}
                    money_format={money_format}
                    additionalAddonProducts={additionalAddonProducts}
                />
            )}
        </>
    );
};

const AddonModal: React.FC<AddonModalProps> = ({
    isOpen,
    onClose,
    additional_product,
    selectedAddon,
    onConfirm,
    onRemove,
    cartConfig,
    loadingAddon,
    localCustomFieldValues,
    setLocalCustomFieldValues,
    money_format,
    additionalAddonProducts,
}) => {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState<string | null>(null);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    // these modals portal to <body>, outside the cart drawer's focus trap, so each gets its own nested trap
    const addonModalRef = useRef<HTMLDivElement>(null);
    const imagePreviewRef = useRef<HTMLDivElement>(null);
    const [selectedOption, setSelectedOption] = useState(selectedAddon || null);

    const isFormValid =
        additional_product?.custom_fields?.every((field) => {
            if (!field.is_required) return true;
            return localCustomFieldValues[field.field_title]?.trim().length > 0;
        }) ?? true;

    const handleFieldChange = (title: string, value: string) => {
        setLocalCustomFieldValues((prev: any) => ({ ...prev, [title]: value }));
    };

    const handleConfirmClick = () => {
        if (!isFormValid) return;
        onConfirm({ product: selectedOption, fieldValues: localCustomFieldValues });
    };

    const handleRemoveClick = () => {
        onRemove();
    };

    const openImagePreview = (imgSrc?: string | null) => {
        if (!imgSrc) return;
        setPreviewImage(imgSrc);
        setIsPreviewOpen(true);
    };

    const closeImagePreview = () => {
        setIsPreviewOpen(false);
        setPreviewImage(null);
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setDropdownOpen(false);
            }
        };

        if (dropdownOpen) {
            document.addEventListener("click", handleClickOutside, true);
        }

        return () => {
            document.removeEventListener("click", handleClickOutside, true);
        };
    }, [dropdownOpen]);

    if (!isOpen) return null;

    return createPortal(
        <>
            <FocusTrap
                focusTrapOptions={{
                    fallbackFocus: () => addonModalRef.current as HTMLDivElement,
                    allowOutsideClick: true,
                    escapeDeactivates: () => {
                        onClose();
                        return false;
                    },
                }}
            >
                <div ref={addonModalRef} tabIndex={-1} className={styles.addonModalOverlay} onClick={onClose}>
                    <div
                        className={styles.addonModalContainer}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            backgroundColor:
                                cartConfig?.additional_product_design?.addon_modal_background_color || "#edeae6",
                        }}
                    >
                        {/* Close Icon */}
                        <div className={styles.addonModalCloseBtn} onClick={onClose} aria-label="Close modal">
                            <IoClose
                                color={cartConfig?.additional_product_design?.addon_modal_title_color || "#8a8a8a"}
                                size={20}
                            />
                        </div>

                        {/* Modal Title */}
                        <div
                            className={styles.addonModalTitle}
                            style={{
                                color: cartConfig?.additional_product_design?.addon_modal_title_color || "#303030",
                                fontSize: cartConfig?.additional_product_design?.addon_modal_title_size || "14px",
                            }}
                        >
                            {additional_product.addon_modal_title}
                        </div>
                        <div
                            className={styles.addonDivider}
                            style={{
                                backgroundColor: `${cartConfig?.additional_product_design?.addon_modal_title_color}10`,
                            }}
                        >
                            &nbsp;
                        </div>

                        <div
                            style={{
                                padding: "20px",
                                paddingTop: "0px",
                            }}
                        >
                            {/* Select Dropdown */}
                            <div className={styles.addonSelectWrapper}>
                                <label
                                    className={styles.addonSelectLabel}
                                    style={{
                                        color:
                                            cartConfig?.additional_product_design?.addon_modal_title_color || "#303030",
                                        fontSize:
                                            `${cartConfig?.additional_product_design?.addon_modal_title_size - 2}px` ||
                                            "14px",
                                    }}
                                >
                                    {additional_product?.addon_selector_label}
                                </label>
                                <div
                                    ref={dropdownRef}
                                    className={styles.addonSelectBox}
                                    onClick={() => setDropdownOpen(!dropdownOpen)}
                                >
                                    {selectedOption ? (
                                        <div className={styles.addonSelectChosen}>
                                            <img
                                                src={selectedOption?.selectedVariants?.[0]?.image?.src || ""}
                                                alt=""
                                                className={styles.addonSelectItemImage}
                                            />
                                            <span className={styles.addonSelectText}>
                                                {selectedOption.selectedVariants?.[0]?.displayName} -{" "}
                                                {money_format.replace(
                                                    /\{\{.*?\}\}/g,
                                                    (
                                                        parseFloat(selectedOption.selectedVariants?.[0]?.price || "0") *
                                                        (window.kefi_bundles?.localization?.rate || 1)
                                                    ).toFixed(2),
                                                )}
                                            </span>
                                        </div>
                                    ) : (
                                        <span className={styles.addonSelectPlaceholder}>Select an option</span>
                                    )}

                                    <span
                                        style={{
                                            position: "absolute",
                                            right: "10px",
                                            top: "50%",
                                            transform: "translateY(-50%)",
                                            pointerEvents: "none",
                                        }}
                                    >
                                        {dropdownOpen ? (
                                            <IoChevronUp
                                                color={
                                                    cartConfig?.additional_product_design?.addon_modal_title_color ||
                                                    "#8a8a8a"
                                                }
                                                size={16}
                                            />
                                        ) : (
                                            <IoChevronDown
                                                color={
                                                    cartConfig?.additional_product_design?.addon_modal_title_color ||
                                                    "#8a8a8a"
                                                }
                                                size={16}
                                            />
                                        )}
                                    </span>
                                    {dropdownOpen && (
                                        <div className={styles.addonSelectMenu}>
                                            {additionalAddonProducts?.map((prod) => (
                                                <div
                                                    key={prod.id}
                                                    style={{
                                                        borderBottom: `1px solid ${cartConfig?.additional_product_design?.addon_modal_title_color}10`,
                                                        borderRadius: "5px",
                                                        marginBottom: "5px",
                                                        listStyle: "none", // prevents default bullet
                                                    }}
                                                >
                                                    {/* If product has multiple variants → render parent label */}
                                                    {prod.variants?.length > 1 ? (
                                                        <>
                                                            {/* Parent label */}
                                                            {/* <div
                                                            className={styles.addonSelectMenuItemParent}
                                                            style={{ cursor: "default" }}
                                                        >
                                                            <div className={styles.addonSelectItemImageWrapper}>
                                                                <img
                                                                    src={prod.images?.[0]?.originalSrc}
                                                                    alt={prod.title || "addon"}
                                                                    className={styles.addonSelectItemImage}
                                                                />
                                                                &nbsp;
                                                            </div>

                                                            <span className={styles.addonSelectItemText}>
                                                                {prod.title} –{" "}
                                                                {(() => {
                                                                    const prices =
                                                                        prod.variants?.map(
                                                                            (v) =>
                                                                                parseFloat(v.price || "0") *
                                                                                (window.kefi_bundles?.localization
                                                                                    ?.rate || 1),
                                                                        ) || [];

                                                                    if (prices.length === 0) return ""; // no price case

                                                                    const minPrice = Math.min(...prices);
                                                                    const maxPrice = Math.max(...prices);

                                                                    if (minPrice === maxPrice) {
                                                                        return money_format.replace(
                                                                            /\{\{.*?\}\}/g,
                                                                            minPrice.toFixed(2),
                                                                        );
                                                                    }

                                                                    return (
                                                                        money_format.replace(
                                                                            /\{\{.*?\}\}/g,
                                                                            minPrice.toFixed(2),
                                                                        ) +
                                                                        " – " +
                                                                        money_format.replace(
                                                                            /\{\{.*?\}\}/g,
                                                                            maxPrice.toFixed(2),
                                                                        )
                                                                    );
                                                                })()}
                                                            </span>
                                                        </div> */}

                                                            {/* Render child variants */}
                                                            <div style={{ paddingLeft: "10px", margin: 0 }}>
                                                                {prod.variants.map((variant) => (
                                                                    <div
                                                                        key={variant.id}
                                                                        className={`${styles.addonSelectMenuItem} `}
                                                                        style={{ cursor: "pointer" }}
                                                                        onMouseDown={(e) => {
                                                                            // e.stopPropagation();
                                                                            setSelectedOption({
                                                                                ...prod,
                                                                                selectedVariants: [variant],
                                                                            });
                                                                            setDropdownOpen(false);
                                                                        }}
                                                                    >
                                                                        <div
                                                                            className={
                                                                                styles.addonSelectItemImageWrapper
                                                                            }
                                                                        >
                                                                            <img
                                                                                src={variant.image?.src || ""}
                                                                                alt={variant.displayName || "addon"}
                                                                                className={styles.addonSelectItemImage}
                                                                            />

                                                                            {/* Eye button */}
                                                                            <button
                                                                                type="button"
                                                                                className={
                                                                                    styles.addonSelectItemPreviewBtn
                                                                                }
                                                                                onMouseDown={(e) => {
                                                                                    e.stopPropagation();
                                                                                    openImagePreview(
                                                                                        variant.image?.src || null,
                                                                                    );
                                                                                }}
                                                                                aria-label={`Preview ${variant.displayName}`}
                                                                            >
                                                                                <IoMdEye
                                                                                    color={
                                                                                        cartConfig
                                                                                            ?.additional_product_design
                                                                                            ?.addon_modal_title_color ||
                                                                                        "#8a8a8a"
                                                                                    }
                                                                                />
                                                                            </button>
                                                                        </div>
                                                                        <span className={styles.addonSelectItemText}>
                                                                            {variant.displayName} –{" "}
                                                                            {money_format.replace(
                                                                                /\{\{.*?\}\}/g,
                                                                                (
                                                                                    parseFloat(variant?.price || "0") *
                                                                                    (window.kefi_bundles?.localization
                                                                                        ?.rate || 1)
                                                                                ).toFixed(2),
                                                                            )}
                                                                        </span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </>
                                                    ) : (
                                                        // Single variant
                                                        <div
                                                            className={styles.addonSelectMenuItem}
                                                            style={{ cursor: "pointer" }}
                                                            onMouseDown={(e) => {
                                                                e.stopPropagation();
                                                                setSelectedOption({
                                                                    ...prod,
                                                                    selectedVariants: [prod.variants[0]],
                                                                });
                                                                setDropdownOpen(false);
                                                            }}
                                                        >
                                                            <div className={styles.addonSelectItemImageWrapper}>
                                                                <img
                                                                    src={prod.images?.[0]?.originalSrc}
                                                                    alt={prod.title || "addon"}
                                                                    className={styles.addonSelectItemImage}
                                                                />

                                                                {/* Eye button */}
                                                                <button
                                                                    type="button"
                                                                    className={styles.addonSelectItemPreviewBtn}
                                                                    onMouseDown={(e) => {
                                                                        e.stopPropagation();
                                                                        openImagePreview(
                                                                            prod.images?.[0]?.originalSrc || null,
                                                                        );
                                                                    }}
                                                                    aria-label={`Preview ${prod.title}`}
                                                                >
                                                                    <IoMdEye
                                                                        color={
                                                                            cartConfig?.additional_product_design
                                                                                ?.addon_modal_title_color || "#8a8a8a"
                                                                        }
                                                                    />
                                                                </button>
                                                            </div>
                                                            <span className={styles.addonSelectItemText}>
                                                                {prod.title} –{" "}
                                                                {money_format.replace(
                                                                    /\{\{.*?\}\}/g,
                                                                    (
                                                                        parseFloat(prod.variants?.[0]?.price || "0") *
                                                                        (window.kefi_bundles?.localization?.rate || 1)
                                                                    ).toFixed(2),
                                                                )}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Custom Fields */}
                            {cartConfig?.additional_product?.custom_fields_checkbox &&
                                additional_product.custom_fields.map((field) => {
                                    if (field.field_type === "input") {
                                        return (
                                            <div key={field.field_title} className={styles.addonCustomFieldWrapper}>
                                                <label
                                                    className={styles.addonCustomFieldLabel}
                                                    style={{
                                                        color:
                                                            cartConfig?.additional_product_design
                                                                ?.addon_modal_title_color || "#303030",
                                                        fontSize:
                                                            `${
                                                                cartConfig?.additional_product_design
                                                                    ?.addon_modal_title_size - 2
                                                            }px` || "12px",
                                                    }}
                                                >
                                                    {field.field_title}
                                                    {field.is_required && (
                                                        <span className={styles.addonRequiredMark}>*</span>
                                                    )}
                                                </label>
                                                <input
                                                    type="text"
                                                    placeholder={field.field_placeholder || ""}
                                                    value={localCustomFieldValues[field.field_title] || ""}
                                                    onChange={(e) =>
                                                        handleFieldChange(field.field_title, e.target.value)
                                                    }
                                                    className={styles.addonCustomFieldInput}
                                                    required={field.is_required}
                                                />
                                            </div>
                                        );
                                    }

                                    if (field.field_type === "textarea") {
                                        return (
                                            <div key={field.field_title} className={styles.addonCustomFieldWrapper}>
                                                <label
                                                    className={styles.addonCustomFieldLabel}
                                                    style={{
                                                        color:
                                                            cartConfig?.additional_product_design
                                                                ?.addon_modal_title_color || "#303030",
                                                        fontSize:
                                                            `${
                                                                cartConfig?.additional_product_design
                                                                    ?.addon_modal_title_size - 2
                                                            }px` || "12px",
                                                    }}
                                                >
                                                    {field.field_title}
                                                    {field.is_required && (
                                                        <span className={styles.addonRequiredMark}>*</span>
                                                    )}
                                                </label>
                                                <textarea
                                                    placeholder={field.field_placeholder || ""}
                                                    value={localCustomFieldValues[field.field_title] || ""}
                                                    onChange={(e) =>
                                                        handleFieldChange(field.field_title, e.target.value)
                                                    }
                                                    className={styles.addonCustomFieldTextarea}
                                                    required={field.is_required}
                                                />
                                            </div>
                                        );
                                    }

                                    return null;
                                })}

                            {/* Footer */}
                            <div className={styles.addonModalFooter}>
                                <button
                                    disabled={loadingAddon}
                                    className={styles.addonModalRemoveBtn}
                                    onClick={handleRemoveClick}
                                    style={{
                                        color:
                                            cartConfig?.additional_product_design
                                                ?.addon_modal_secondary_button_text_color || "#ffffff",
                                    }}
                                >
                                    Remove
                                </button>
                                <button
                                    disabled={!isFormValid || loadingAddon}
                                    loading={loadingAddon}
                                    className={styles.addonModalConfirmBtn}
                                    onClick={handleConfirmClick}
                                    style={{
                                        backgroundColor:
                                            cartConfig?.additional_product_design?.addon_modal_primary_button_bg ||
                                            "#B98B37",
                                        color:
                                            cartConfig?.additional_product_design
                                                ?.addon_modal_primary_button_text_color || "#ffffff",
                                        opacity: !isFormValid ? 0.6 : 1,
                                    }}
                                >
                                    Confirm
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </FocusTrap>

            {/* --- Image Preview Modal --- */}
            {isPreviewOpen &&
                createPortal(
                    <FocusTrap
                        focusTrapOptions={{
                            fallbackFocus: () => imagePreviewRef.current as HTMLDivElement,
                            allowOutsideClick: true,
                            escapeDeactivates: () => {
                                closeImagePreview();
                                return false;
                            },
                        }}
                    >
                        <div
                            ref={imagePreviewRef}
                            tabIndex={-1}
                            className={styles.imagePreviewOverlay}
                            onClick={closeImagePreview}
                            role="dialog"
                            aria-modal="true"
                        >
                            <div className={styles.imagePreviewContainer} onClick={(e) => e.stopPropagation()}>
                                <button
                                    className={styles.imagePreviewCloseBtn}
                                    onClick={closeImagePreview}
                                    aria-label="Close preview"
                                >
                                    <IoClose color="#8a8a8a" size={20} />
                                </button>

                                {previewImage ? (
                                    <div className={styles.imagePreviewWrapper}>
                                        <img src={previewImage} alt="Preview" className={styles.imagePreviewImg} />
                                    </div>
                                ) : (
                                    <div className={styles.imagePreviewEmpty}>No image</div>
                                )}
                            </div>
                        </div>
                    </FocusTrap>,
                    document.body,
                )}
        </>,
        document.body,
    );
};

export default AdditionalProductComponent;
